import React, { useState } from 'react';
import { COUNCIL_SESSION_OPTIONS, filterResourcesByCouncil } from '../utils/resourceFilters';

const YEAR_OPTIONS = ['Year 1', 'Year 2'];

function fileIcon(fileType) {
  const t = String(fileType || '').toLowerCase();
  if (t === 'ppt' || t === 'pptx') return '📊';
  if (t === 'pdf') return '📄';
  if (t === 'mp4' || t === 'mov' || t === 'webm' || t === 'video') return '🎬';
  return '📎';
}

function isPdf(fileType) {
  return String(fileType || '').toLowerCase() === 'pdf';
}

function isVideo(fileType) {
  return ['mp4', 'mov', 'webm', 'video'].includes(String(fileType || '').toLowerCase());
}

export default function ResourcesViewer({ resources }) {
  const [levelYear, setLevelYear] = useState('Year 1');
  const [sessionType, setSessionType] = useState('Orientation');
  const [openResource, setOpenResource] = useState(null);

  const matched = filterResourcesByCouncil(resources, sessionType, levelYear);

  return (
    <div className="form-field-group" style={{ maxWidth: '900px' }}>
      <h2 style={{ marginBottom: '4px', color: 'var(--accent-orange)' }}>Learning Space</h2>
      <p style={{ color: 'var(--text-muted)', fontSize: '13px', marginBottom: '20px' }}>
        Select a year and council session to view its resources.
      </p>

      <div style={{ display: 'flex', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '6px' }}>Year</label>
          <select
            value={levelYear}
            onChange={(e) => { setLevelYear(e.target.value); setOpenResource(null); }}
            style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', minWidth: '140px' }}
          >
            {YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '6px' }}>Council Session</label>
          <select
            value={sessionType}
            onChange={(e) => { setSessionType(e.target.value); setOpenResource(null); }}
            style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', minWidth: '180px' }}
          >
            {COUNCIL_SESSION_OPTIONS.map(opt => (
              <option key={opt.sessionType} value={opt.sessionType}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      {matched.length === 0 ? (
        <div style={{ color: 'var(--text-muted)', fontSize: '13px', fontStyle: 'italic', padding: '14px 16px', background: 'var(--neutral-50)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
          No resources found for {levelYear} — {sessionType}.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {matched.map(res => (
            <div
              key={res.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '10px 14px',
                background: 'rgba(1, 71, 162, 0.03)',
                border: '1px solid rgba(1, 71, 162, 0.18)',
                borderRadius: '6px',
              }}
            >
              <span style={{ fontSize: '18px', lineHeight: 1 }}>{fileIcon(res.fileType)}</span>
              <span style={{ flexGrow: 1, fontSize: '14px', color: 'var(--neutral-800)', fontWeight: 500 }}>{res.title}</span>
              <span className="checklist-row-badge" style={{ margin: 0, flexShrink: 0 }}>{String(res.fileType || '').toUpperCase()}</span>
              {(isPdf(res.fileType) || isVideo(res.fileType)) && res.viewUrl && (
                <button
                  type="button"
                  className="btn-secondary-action"
                  style={{ fontSize: '13px', padding: '4px 10px', flexShrink: 0 }}
                  onClick={() => setOpenResource(res)}
                >
                  Open
                </button>
              )}
              {!(isPdf(res.fileType) || isVideo(res.fileType)) && res.viewUrl && (
                <a href={res.viewUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px', textDecoration: 'underline', flexShrink: 0 }}>Preview</a>
              )}
              {res.downloadUrl && (
                <a href={res.downloadUrl} download style={{ fontSize: '13px', textDecoration: 'underline', flexShrink: 0 }}>Download</a>
              )}
            </div>
          ))}
        </div>
      )}

      {openResource && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
          onClick={() => setOpenResource(null)}
        >
          <div
            style={{
              background: '#ffffff', borderRadius: '10px', width: '90vw', maxWidth: '1000px',
              height: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 18px', borderBottom: '1px solid var(--border-color)' }}>
              <span style={{ fontSize: '18px' }}>{fileIcon(openResource.fileType)}</span>
              <span style={{ flexGrow: 1, fontWeight: 600, fontSize: '15px' }}>{openResource.title}</span>
              {openResource.downloadUrl && (
                <a href={openResource.downloadUrl} download className="btn-secondary-action" style={{ fontSize: '13px', padding: '6px 12px' }}>
                  Download
                </a>
              )}
              <button type="button" className="btn-secondary-action" style={{ fontSize: '13px', padding: '6px 12px' }} onClick={() => setOpenResource(null)}>
                Close
              </button>
            </div>
            <div style={{ flexGrow: 1, background: '#1a1a1a' }}>
              {isPdf(openResource.fileType) && (
                <iframe
                  src={openResource.viewUrl}
                  title={openResource.title}
                  style={{ width: '100%', height: '100%', border: 'none' }}
                />
              )}
              {isVideo(openResource.fileType) && (
                <video
                  key={openResource.id}
                  src={openResource.viewUrl}
                  controls
                  autoPlay
                  style={{ width: '100%', height: '100%', background: '#000' }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
